import React, { useEffect } from 'react';
import { ContentfulEntry, ContentfulPageListing } from '@jbhi-fi/apis/src/contentful/types';
import { useInfiniteQuery } from 'react-query';
import { ContentfulThemeApiClient } from '@jbhi-fi/apis/src/contentfulThemeApi';
import { usePublicApiClient } from '../hooks/usePublicApiClient';
import { AlphabeticalLinksList } from '../AlphabeticalLinksList/AlphabeticalLinksList';
import LoadingIcon from '@jbhi-fi/lanyard-ui/components/Icons/Loading';
import { styles } from './ContentfulPagesList.css';

type ContentfulPageListingFields = Exclude<ContentfulPageListing['fields'], undefined>;
type CollectionFromQueryResult = ContentfulEntry<{ handle: string; title: string }>;

export type ContentfulPagesListProps = {
  pageType?: ContentfulPageListingFields['pageTypeToQuery'];
  collectionType: ContentfulPageListingFields['collectionType'];
  order: 'asc' | 'desc';
  contentfulThemeApiClient?: ContentfulThemeApiClient;
};

export const ContentfulPagesList = ({
  pageType = 'Collections',
  collectionType = 'Seller',
  contentfulThemeApiClient: contentfulThemeApiClientProp,
  order,
}: ContentfulPagesListProps) => {
  const { contentfulThemeApiClient: contentfulThemeApiClientContext } = usePublicApiClient();
  const contentfulThemeApiClient = contentfulThemeApiClientProp || contentfulThemeApiClientContext;
  if (!contentfulThemeApiClient) {
    throw new Error('ContentfulPagesList: contentfulThemeApiClient is required');
  }
  const [isLoading, setIsLoading] = React.useState(true);
  const {
    data,
    isFetchingNextPage,
    isLoading: isQueryLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['contentfulPagesList', pageType, collectionType, order],
    queryFn: ({ pageParam = 0 }) =>
      contentfulThemeApiClient.getEntries({
        ...buildContentfulEntriesQuery({ pageType, collectionType, order }),
        include: '1',
        skip: `${(pageParam as number) * 100}`,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) return 0;
      if ((lastPage.data?.items?.length ?? 0) < 100) return undefined;
      return allPages.length;
    },
    keepPreviousData: true,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const pages = (data?.pages?.flatMap(page => page?.data?.items ?? []) ?? []) as Array<CollectionFromQueryResult>;

  useEffect(() => {
    /* Can only fetch 100 entries at a time from contentful, so just keep fetching in batches until we have everything */
    if (isQueryLoading || isFetchingNextPage) return;
    if (hasNextPage) {
      fetchNextPage();
    } else {
      setIsLoading(false);
    }
  }, [hasNextPage, isQueryLoading, isFetchingNextPage]);

  return (
    <div>
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <LoadingIcon width="3rem" height="3rem" />
        </div>
      ) : (
        <AlphabeticalLinksList
          links={pages.map(page => ({
            href: buildHref(pageType, page.fields?.handle ?? ''),
            label: page.fields?.title ?? '',
          }))}
          order={order}
        />
      )}
    </div>
  );
};

const buildHref = (pageType: ContentfulPagesListProps['pageType'], handle: string) => {
  switch (pageType) {
    case 'Collections':
    default:
      return `/collections/${handle}`;
  }
};

const buildContentfulEntriesQuery = ({
  pageType,
  collectionType = 'Seller',
  order,
}: Pick<ContentfulPagesListProps, 'pageType' | 'collectionType' | 'order'>) => {
  switch (pageType) {
    case 'Collections':
    default:
      return {
        content_type: 'smartCollection',
        select: 'fields.handle,fields.title',
        order: `${order === 'desc' ? '-' : ''}fields.title`,
        'fields.template': collectionType,
      };
  }
};
